/**
 * Sport → background art mapping, shared by the competition/game page headers
 * and the Team Finder result cards so a competition with no banner image of its
 * own falls back to the same artwork everywhere.
 *
 * Returns an EXTENSION-LESS path (e.g. '/images/grass') because the page-header
 * callers append their own extension. Use `getSportBackgroundImageUrl` when you
 * want a ready-to-render `.png` url.
 */

/** Minimal shape this mapper needs off a Redux sport record. */
export interface SportLike {
  name?: string | null
}

/** Minimal shape this mapper needs off a generic-sport record. */
export interface GenericSportLike {
  background_web_image?: string | null
}

export interface SportBackgroundArgs {
  /** The generic-sport record, when the competition is a basic-stats sport. */
  genericSport?: GenericSportLike | null
  /** Whether this is the full-stats soccer sport. */
  isSoccer?: boolean
  /** The full-stats sport record (its `name` drives the per-sport art). */
  sport?: SportLike | null
}

export const HOCKEY_BACKGROUND = '/images/game_hockey'
export const SOCCER_BACKGROUND = '/images/soccer_game_background'
export const GRASS_BACKGROUND = '/images/grass'

// Full-stats sports whose art isn't the hockey default. Sports that play on a
// grass field share the same grass image.
const SPORT_NAME_BACKGROUNDS: Record<string, string> = {
  lacrosse: GRASS_BACKGROUND,
  fieldhockey: GRASS_BACKGROUND,
  floorball: '/images/floorball',
  ballhockey: '/images/ballhockey',
}

/**
 * Extension-less background path for a sport. A generic (basic-stats) sport
 * carries its own `background_web_image`; otherwise the art is chosen from the
 * full-stats sport's name, defaulting to hockey.
 */
export const getSportBackgroundImage = ({
  genericSport,
  isSoccer,
  sport,
}: SportBackgroundArgs): string => {
  if (genericSport?.background_web_image) {
    return genericSport.background_web_image.replace('.png', '')
  }
  if (isSoccer) {
    return SOCCER_BACKGROUND
  }
  const name = sport?.name
  if (name && SPORT_NAME_BACKGROUNDS[name]) {
    return SPORT_NAME_BACKGROUNDS[name]
  }
  return HOCKEY_BACKGROUND
}

/** Same mapping, as a ready-to-render `.png` url. */
export const getSportBackgroundImageUrl = (args: SportBackgroundArgs): string =>
  `${getSportBackgroundImage(args)}.png`
